import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { trpc } from "../lib/trpc";

/** Matches the server's 60 s magic-link resend cooldown (requestMagicLink). */
const RESEND_COOLDOWN_MS = 60_000;

/** Seconds left on the resend countdown, ticking once a second; 0 when idle. */
function useResendCountdown() {
  const [deadline, setDeadline] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (deadline === null) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= deadline) {
        setDeadline(null);
        clearInterval(id);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [deadline]);
  const secondsLeft = deadline === null ? 0 : Math.ceil((deadline - now) / 1000);
  return {
    clear: () => setDeadline(null),
    // Shown as Resend 0:59 … 0:01 (the first second rounds down to 59).
    secondsLeft: Math.max(0, Math.min(59, secondsLeft)),
    start: () => {
      const t = Date.now();
      setNow(t);
      setDeadline(t + RESEND_COOLDOWN_MS);
    },
  };
}

/**
 * Login page - Magic link authentication
 */
export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const countdown = useResendCountdown();
  // The address the running countdown belongs to; changing it clears the timer.
  const [sentTo, setSentTo] = useState<string | null>(null);

  const requestMagicLink = trpc.auth.requestMagicLink.useMutation({
    onSuccess: (data) => {
      // TeleCloud addresses sign in with SAML only (LP-029); no link was sent.
      if ("useSaml" in data) {
        navigate("/admin/login");
        return;
      }
      countdown.start();
      setIsSubmitted(true);
      toast.success("Magic link sent! Check your email.");
    },
    onError: (error) => {
      countdown.start();
      toast.error(error.message || "Failed to send magic link");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    requestMagicLink.mutate({ email });
    setSentTo(email.trim().toLowerCase());
  };

  const resendLabel =
    countdown.secondsLeft > 0 ? `Resend 0:${String(countdown.secondsLeft).padStart(2, "0")}` : "Resend";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-accent/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        {/* Hero Section */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-brand-primary rounded-2xl mb-6 shadow-glow-primary animate-glow-pulse"
          >
            <Sparkles className="w-10 h-10 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-bold text-brand-primary mb-3"
          >
            TeleCloud Launchpad
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-muted-foreground text-lg"
          >
            Premium VoIP Provisioning Platform
          </motion.p>
        </div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card variant="glass">
            <CardHeader>
              <CardTitle>Welcome back</CardTitle>
              <CardDescription>
                {isSubmitted
                  ? "Check your email for the magic link"
                  : "Enter your email to receive a magic link"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-center py-8"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.1 }}
                    className="inline-flex items-center justify-center w-20 h-20 bg-brand-success rounded-full mb-6 shadow-glow-success"
                  >
                    <Mail className="w-10 h-10 text-white" />
                  </motion.div>

                  <h3 className="text-xl font-semibold mb-3">Check your email</h3>
                  <p className="text-sm text-muted-foreground mb-8">
                    We've sent a magic link to<br />
                    <strong className="text-foreground">{email}</strong>
                  </p>

                  <div className="flex flex-col items-center gap-3">
                    <Button
                      variant="brand"
                      disabled={requestMagicLink.isPending || countdown.secondsLeft > 0}
                      onClick={() => requestMagicLink.mutate({ email })}
                    >
                      {requestMagicLink.isPending ? "Sending..." : resendLabel}
                    </Button>
                    <Button variant="outline-brand" onClick={() => setIsSubmitted(false)}>
                      Use a different email
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" required>Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (e.target.value.trim().toLowerCase() !== sentTo) countdown.clear();
                      }}
                      required
                      disabled={requestMagicLink.isPending}
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="brand"
                    size="lg"
                    className="w-full"
                    disabled={requestMagicLink.isPending || !email || countdown.secondsLeft > 0}
                  >
                    {requestMagicLink.isPending ? (
                      <>Sending magic link...</>
                    ) : countdown.secondsLeft > 0 ? (
                      <>Resend 0:{String(countdown.secondsLeft).padStart(2, "0")}</>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Send magic link
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center text-sm text-muted-foreground mt-8"
        >
          Don't have an account? Contact your administrator for an invitation.
        </motion.p>
      </motion.div>
    </div>
  );
}
